import {useEffect, useState} from 'react'
import {Search} from 'lucide-react'
import {Text} from '@/components/tailwind/text'
import {Button} from '@/components/tailwind/button'
import {Badge} from '@/components/tailwind/badge'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/tailwind/table'
import {Input, InputGroup} from '@/components/tailwind/input'
import {Dialog, DialogActions, DialogBody, DialogTitle} from '@/components/tailwind/dialog'
import {useAdminTranslation} from "@/i18n/hooks"
import {useAuth} from '@/context/use-auth'
import LoadingSpinner from "@/components/loader-spinner"
import {getLatestCleanupRun, getCleanupLog} from '@/services/data-cleanup-services'
import type {CleanupRunResponse, CleanupLogEntry} from '@/models/data-cleanup'
import {formatDateString} from '@/lib/utils'
import {Subheading} from '@/components/tailwind/heading'

export function SectionDataCleanup() {
    const {t} = useAdminTranslation()
    const {token} = useAuth()

    const [loading, setLoading] = useState(true)
    const [latestRun, setLatestRun] = useState<CleanupRunResponse | null>(null)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [logEntries, setLogEntries] = useState<CleanupLogEntry[]>([])
    const [logLoading, setLogLoading] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')

    useEffect(() => {
        if (token) {
            setLoading(true)
            getLatestCleanupRun(token)
                .then(setLatestRun)
                .finally(() => setLoading(false))
        }
    }, [token])

    function openLogDialog() {
        setIsDialogOpen(true)
        loadLog()
    }

    function loadLog(search?: string) {
        if (!token) return
        setLogLoading(true)
        getCleanupLog(token, search)
            .then(setLogEntries)
            .finally(() => setLogLoading(false))
    }

    function handleSearchChange(value: string) {
        setSearchQuery(value)
    }

    function handleSearchSubmit() {
        loadLog(searchQuery)
    }

    function getStatusBadge(run: CleanupRunResponse) {
        if (run.status === 'SUCCESS') {
            return <Badge color="green">{t('adminSettings.dataCleanup.status.SUCCESS')}</Badge>
        }
        if (run.status === 'FAILED') {
            return <Badge color="red">{t('adminSettings.dataCleanup.status.FAILED')}</Badge>
        }
        return <Badge color="yellow">{t('adminSettings.dataCleanup.status.RUNNING')}</Badge>
    }

    function getTriggerLabel(triggerType: string) {
        switch (triggerType) {
            case 'STARTUP':
                return t('adminSettings.dataCleanup.trigger.STARTUP')
            case 'SCHEDULED':
                return t('adminSettings.dataCleanup.trigger.SCHEDULED')
            case 'MANUAL':
                return t('adminSettings.dataCleanup.trigger.MANUAL')
            default:
                return triggerType
        }
    }

    if (loading) {
        return (
            <div>
                <Subheading>{t('adminSettings.dataCleanup.title')}</Subheading>
                <Text className="mt-1">{t('adminSettings.dataCleanup.description')}</Text>
                <div className="mt-4">
                    <LoadingSpinner/>
                </div>
            </div>
        )
    }

    return (
        <div>
            <Subheading>{t('adminSettings.dataCleanup.title')}</Subheading>
            <Text className="mt-1">{t('adminSettings.dataCleanup.description')}</Text>

            <div className="mt-4 rounded-lg border border-zinc-200 dark:border-zinc-700 p-4">
                {latestRun ? (
                    <div className="space-y-2">
                        <div className="flex items-center gap-3">
                            {getStatusBadge(latestRun)}
                            <Text className="text-sm">
                                {t('adminSettings.dataCleanup.lastRun', {
                                    date: formatDateString(latestRun.startedAt),
                                    trigger: getTriggerLabel(latestRun.triggerType),
                                    count: latestRun.membersCleaned,
                                })}
                            </Text>
                        </div>
                        {latestRun.status === 'FAILED' && latestRun.errorMessage && (
                            <div className="mt-2 rounded-md bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 p-3">
                                <Text className="text-sm text-red-700 dark:text-red-300">
                                    {latestRun.errorMessage}
                                </Text>
                            </div>
                        )}
                    </div>
                ) : (
                    <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                        {t('adminSettings.dataCleanup.neverRun')}
                    </Text>
                )}

                <div className="mt-4">
                    <Button outline onClick={openLogDialog}>
                        {t('adminSettings.dataCleanup.logButton')}
                    </Button>
                </div>
            </div>

            <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} size="4xl">
                <DialogTitle>{t('adminSettings.dataCleanup.logDialog.title')}</DialogTitle>
                <DialogBody>
                    <div className="mb-4">
                        <InputGroup>
                            <Search data-slot="icon"/>
                            <Input
                                type="text"
                                placeholder={t('adminSettings.dataCleanup.logDialog.searchPlaceholder')}
                                value={searchQuery}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSearchSubmit()
                                }}
                            />
                        </InputGroup>
                    </div>

                    {logLoading ? (
                        <div className="flex justify-center py-8">
                            <LoadingSpinner/>
                        </div>
                    ) : logEntries.length === 0 ? (
                        <Text className="text-center py-8 text-zinc-500 dark:text-zinc-400">
                            {t('adminSettings.dataCleanup.logDialog.noEntries')}
                        </Text>
                    ) : (
                        <div className="max-h-96 overflow-y-auto">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>{t('adminSettings.dataCleanup.logDialog.columns.memberNo')}</TableHeader>
                                        <TableHeader>{t('adminSettings.dataCleanup.logDialog.columns.firstName')}</TableHeader>
                                        <TableHeader>{t('adminSettings.dataCleanup.logDialog.columns.lastName')}</TableHeader>
                                        <TableHeader>{t('adminSettings.dataCleanup.logDialog.columns.membershipEnd')}</TableHeader>
                                        <TableHeader>{t('adminSettings.dataCleanup.logDialog.columns.cleanedAt')}</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logEntries.map((entry, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{entry.memberNo}</TableCell>
                                            <TableCell>{entry.firstName}</TableCell>
                                            <TableCell>{entry.lastName}</TableCell>
                                            <TableCell>
                                                {entry.membershipEnd
                                                    ? new Date(entry.membershipEnd).toLocaleDateString('de-DE')
                                                    : '-'
                                                }
                                            </TableCell>
                                            <TableCell>{formatDateString(entry.cleanedAt)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </DialogBody>
                <DialogActions>
                    <Button plain onClick={() => setIsDialogOpen(false)}>
                        {t('adminSettings.dataCleanup.logDialog.close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
